import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, inject, input } from '@angular/core';
import { ReplaySubject, Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { SwiperOptions } from 'swiper';
import { SwiperModule } from 'swiper/angular';

import {
  LARGE_BREAKPOINT_WIDTH,
  MEDIUM_BREAKPOINT_WIDTH,
  SMALL_BREAKPOINT_WIDTH,
} from 'ish-core/configurations/injection-keys';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { ContentPageletView } from 'ish-core/models/content-view/content-view.model';
import { whenTruthy } from 'ish-core/utils/operators';
import { CMSComponent } from 'ish-shared/cms/models/cms-component/cms-component.model';
import { CustomCategoryTileComponent } from 'ish-shared/dias/components/category/custom-category-tile/custom-category-tile.component';
import { CategoryContextDirective } from 'ish-core/directives/category-context.directive';

// This is customized component for ICSA
@Component({
  selector: 'custom-cms-category-list-manual',
  templateUrl: './cms-category-list-manual.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, SwiperModule, CustomCategoryTileComponent,CategoryContextDirective],
})
export class CMSCategoryListManualComponent implements OnChanges, OnDestroy, CMSComponent {
  private shoppingFacade = inject(ShoppingFacade);
  private smallBreakpointWidth = inject(SMALL_BREAKPOINT_WIDTH);
  private mediumBreakpointWidth = inject(MEDIUM_BREAKPOINT_WIDTH);
  private largeBreakpointWidth = inject(LARGE_BREAKPOINT_WIDTH);

  readonly pagelet = input<ContentPageletView>(undefined);
  listStyle: string;
  categoryRefIDs: string[];
  CSSclass: string;
  listItemCSSClass: string;
  /**
   * configuration of swiper carousel
   * https://swiperjs.com/swiper-api
   */
  swiperConfig: SwiperOptions;
  categoryIDs$ = new ReplaySubject<string[]>(1);
  private destroy$ = new Subject<void>();
  private categoryDestroy$ = new Subject<void>();

  constructor() {
    this.swiperConfig = {
      direction: 'horizontal',
      navigation: true,
      pagination: {
        clickable: true,
      },
      observer: true,
      observeParents: true,
    };
  }

  ngOnChanges() {
    const pagelet = this.pagelet();
    this.categoryRefIDs = pagelet.hasParam('Category')
      ? pagelet.configParam<string[]>('Category').map(categoryID => categoryID)
      : [];
    this.getCategoryIDs(this.categoryRefIDs);
    const pageletValue = this.pagelet();
    this.configureSlides(pageletValue.numberParam('SlideItems'));
    this.listStyle = pageletValue.stringParam('ListStyle');
    this.CSSclass = pageletValue.stringParam('CSSClass');
    this.listItemCSSClass = pageletValue.stringParam('ListItemCSSClass');
    this.categoryIDs$.pipe(takeUntil(this.categoryDestroy$)).subscribe(items => {
      if (items.length > this.pagelet().numberParam('SlideItems')) {
        this.swiperConfig = {
          direction: 'horizontal',
          navigation: true,
          pagination: {
            clickable: true,
          },
          observer: true,
          observeParents: true,
          loop: true,
        };
        this.configureSlides(this.pagelet().numberParam('SlideItems'));
        this.destroyCategoryIDsSubscription();
      }
    });
  }
  getCategoryIDs(categoryRefIDs: string[]) {
    const ids: string[] = [];
    categoryRefIDs.map(x =>
      this.shoppingFacade
        .categoryIdByRefId$(x)
        .pipe(whenTruthy(), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(data => {
          ids.push(data);
          this.categoryIDs$.next(ids);
        })
    );
  }

  destroyCategoryIDsSubscription() {
    this.categoryDestroy$.next();
    this.categoryDestroy$.complete();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.categoryDestroy$.next();
    this.categoryDestroy$.complete();
  }

  /**
   * Configure Swiper slidesPerView/slidesPerGroup settings
   * with breakpoint responsive design considerations based on the given slide items.
   *
   * @param slideItems The amount of slide items that should be rendered if enough screen space is available.
   */
  configureSlides(slideItems: number) {
    switch (slideItems) {
      case 1: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
        };
        break;
      }
      case 2: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
        };
        break;
      }
      case 3: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          [this.mediumBreakpointWidth]: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
        };
        break;
      }
      default: {
        this.swiperConfig.breakpoints = {
          0: {
            slidesPerView: 1,
            slidesPerGroup: 1,
          },
          [this.smallBreakpointWidth]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
          [this.mediumBreakpointWidth]: {
            slidesPerView: 3,
            slidesPerGroup: 3,
          },
          [this.largeBreakpointWidth]: {
            slidesPerView: 4,
            slidesPerGroup: 4,
          },
        };
      }
    }
  }
}
